import { IMilestone } from '../typings/interfaces/milestones.interface';
import { IEditableRecord } from './../services/batch-edit-service/batch-edit-service';
import { SorterColumnDataType } from './sorter-column-data-type.constant';

export interface IMilestoneEditable extends IEditableRecord {
  tooltip: string;
  collegeOrder: number;
  careerOrder: number;
  dueBy: string;
  flagType: string;
  isCalculated: boolean;
}

const milestoneStatusOptions = ['Complete', 'Incomplete'];

// if key has a non-null value for collegeOrder/careerOrder
// the milestone exists in that track (JE)
export const MilestonesKeyMap = {
  PROFESSIONAL_EMAIL: {
    humanName: 'Professional email',
    tooltip: 'Student has created an email account that follows a standard format (ie, firstname@domain.org).',
    collegeOrder: 2,
    careerOrder: 4,
    dueBy: 'End of Soph',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  COLLEGE_FAIR: {
    humanName: 'College fair',
    tooltip: 'Student has attended a college fair.',
    collegeOrder: 3,
    careerOrder: null,
    dueBy: 'End of Junior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  FINALIZED_PERSONAL_STATEMENT: {
    humanName: 'Finalized personal statement',
    tooltip: 'Student has a finalized personal statement.',
    collegeOrder: 4,
    careerOrder: null,
    dueBy: 'Oct 1 of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  FINALIZED_LETTER_OF_REC: {
    humanName: 'Finalized letters of recommendation',
    tooltip: 'Student has two finalized letters of recommedations.',
    collegeOrder: 5,
    careerOrder: null,
    dueBy: 'Oct 1 of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  CREATED_POSTSECONDARY_LIST: {
    humanName: 'Created postsecondary list',
    tooltip: `Student has documented a list of colleges (and career pathways if applicable) where they plan to apply.`,
    collegeOrder: 6,
    careerOrder: null,
    dueBy: 'Oct 18 of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  OPP_PROGRAM_ELL_CONFIRM: {
    humanName: 'EOP financial eligibility confirmation',
    tooltip: 'Parent income information has been reviewed and EOP eligibility has been confirmed in the Portal.',
    collegeOrder: 7,
    careerOrder: null,
    dueBy: 'Oct 18 of Senior',
    flagType: 'hard',
    isCalculated: true,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
  } as IMilestoneEditable,
  CUNY_APP_SUBMISSION: {
    humanName: 'CUNY application fee submission',
    tooltip: 'Student has paid CUNY application fee or has used a fee waiver.',
    collegeOrder: 8,
    careerOrder: null,
    dueBy: 'Nov 21 of Senior',
    flagType: 'hard',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  FAFSA_APP_SUBMISSION: {
    humanName: 'FAFSA application submission',
    tooltip: 'Student has submitted FAFSA application and the application has been successfully processed.',
    collegeOrder: 9,
    careerOrder: null,
    dueBy: 'Feb 1 of Senior',
    flagType: 'hard',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  TAP_APP_SUBMISSION: {
    humanName: 'TAP application submission',
    tooltip: 'Student has submitted TAP application and the application has been successfully processed.',
    collegeOrder: 10,
    careerOrder: null,
    dueBy: 'Feb 1 of Senior',
    flagType: 'hard',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  COUNSELING_FINAL_DECISION: {
    humanName: 'Final decision conversation',
    tooltip: 'Student and counselor have met to make a final postsecondary decision.',
    collegeOrder: 11,
    careerOrder: 12,
    dueBy: 'May 1 of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  FINAL_DECISION: {
    humanName: 'Final decision',
    tooltip: `Counselor has entered the student's final postsecondary decision in the Portal.`,
    collegeOrder: 12,
    careerOrder: 13,
    dueBy: 'May 1 of Senior',
    flagType: 'hard',
    isCalculated: true,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
  } as IMilestoneEditable,
  COUNSELING_TRANSITION: {
    humanName: 'Completed transition plan',
    tooltip:
      `Counselor has met with student (and caregiver) to discuss the student’s next steps ` +
      `for a successful postsecondary transition. A transition plan has been documented.`,
    collegeOrder: 13,
    careerOrder: 14,
    dueBy: 'June 10 of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  PICTURE_ID: {
    humanName: 'Valid picture ID',
    tooltip: 'Student has a valid picture ID.',
    collegeOrder: null,
    careerOrder: 3,
    dueBy: 'End of Soph',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  WORKING_PAPERS: {
    humanName: 'Working papers',
    tooltip: 'Any student under 18 (minors) must have an employment certificate before they may begin work.',
    collegeOrder: null,
    careerOrder: 5,
    dueBy: 'End of Soph',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  FINALIZED_RESUME: {
    humanName: 'Finalized resume',
    tooltip: 'Student has an updated and accurate resume.',
    collegeOrder: null,
    careerOrder: 9,
    dueBy: 'Oct 1 of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  ANNUAL_CAREER_INTEREST_SURVEY: {
    humanName: 'Annual career interest survey',
    tooltip:
      'Student has explored careers and interests through research on pathways and analysis of their skills and interests.',
    collegeOrder: null,
    careerOrder: 2,
    dueBy: 'End of May',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  ANNUAL_POSTSECONDARY_PLAN: {
    humanName: 'Annual postsecondary plan',
    tooltip:
      'Student has documented interests, goals and accomplishments in a document that can be saved and added to each year.',
    collegeOrder: 1,
    careerOrder: 1,
    dueBy: 'End of May',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  APPLIED_FOR_SUMMER_JOB: {
    humanName: 'Applied for summer job',
    tooltip: 'Student has submitted applications for SYEP or other summer career development experience.',
    collegeOrder: null,
    careerOrder: 6,
    dueBy: 'End of Junior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  COMPLETED_SUMMER_JOB: {
    humanName: 'Completed summer job',
    tooltip:
      'Student has participated in SYEP or other program, paid or unpaid summer program that provides work experience.',
    collegeOrder: null,
    careerOrder: 7,
    dueBy: 'Oct 1 of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  COVER_LETTER: {
    humanName: 'Cover letter',
    tooltip: 'Student has drafted a well written letter introducing themselves to prospective employers.',
    collegeOrder: null,
    careerOrder: 8,
    dueBy: 'End of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  LINKEDIN_PROFILE: {
    humanName: 'LinkedIn profile',
    tooltip: `Student has created a LinkedIn profile that contains the student's professional contact information and details on their work experience.`,
    collegeOrder: null,
    careerOrder: 10,
    dueBy: 'End of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
  EMPLOYABILITY_SKILLS_PROFILE: {
    humanName: 'Employability skills profile',
    tooltip: `Staff have worked with students' CDOS aligned WBL supervisor to complete required employability skills profile.`,
    collegeOrder: null,
    careerOrder: 11,
    dueBy: 'End of Senior',
    flagType: 'soft',
    isCalculated: false,
    editable: true,
    dataType: SorterColumnDataType.MILESTONE,
    dataTypeOptions: {
      values: milestoneStatusOptions,
    },
    category: 'MILESTONES, POSTSECONDARY',
  } as IMilestoneEditable,
};

export const careerMilestones = {
  ANNUAL_CAREER_INTEREST_SURVEY: 'annualCareerInterestSurvey',
  ANNUAL_POSTSECONDARY_PLAN: 'annualPostsecondaryPlan',
  PICTURE_ID: 'pictureId',
  PROFESSIONAL_EMAIL: 'professionalEmail',
  WORKING_PAPERS: 'workingPapers',
  APPLIED_FOR_SUMMER_JOB: 'appliedForSummerJob',
  COMPLETED_SUMMER_JOB: 'completedSummerJob',
  COVER_LETTER: 'coverLetter',
  FINALIZED_RESUME: 'finalizedResume',
  LINKEDIN_PROFILE: 'linkedInProfile',
  EMPLOYABILITY_SKILLS_PROFILE: 'employabilitySkillsProfile',
  COUNSELING_FINAL_DECISION: 'counselingFinalDecision',
  FINAL_DECISION: 'finalDecision',
  COUNSELING_TRANSITION: 'counselingTransition',
};

export const collegeMilestones = {
  ANNUAL_POSTSECONDARY_PLAN: 'annualPostsecondaryPlan',
  CREATED_POSTSECONDARY_LIST: 'createdPostsecondaryList',
  PROFESSIONAL_EMAIL: 'professionalEmail',
  COLLEGE_FAIR: 'collegeFair',
  FINALIZED_PERSONAL_STATEMENT: 'finalizedPersonalStatement',
  FINALIZED_LETTER_OF_REC: 'finalizedLetterOfRec',
  OPP_PROGRAM_ELL_CONFIRM: 'oppProgramEllConfirm',
  CUNY_APP_SUBMISSION: 'cunyAppSubmission',
  FAFSA_APP_SUBMISSION: 'fafsaAppSubmission',
  TAP_APP_SUBMISSION: 'tapAppSubmission',
  COUNSELING_FINAL_DECISION: 'counselingFinalDecision',
  FINAL_DECISION: 'finalDecision',
  COUNSELING_TRANSITION: 'counselingTransition',
};

export interface IMilestoneDataObj {
  milestone: {
    data: string;
    dependencies: {
      checked: boolean;
      isCalculated: boolean;
      docId: string;
      disabled: boolean;
    };
    collegeOrder: number;
    careerOrder: number;
    tooltipMsg: string;
    tooltipClass: string;
  };
  status: {
    data: IMilestone;
    dependencies: {
      dueAt: IMilestone['dueAt'];
    };
  };
  dueBy: {
    data: string; // '-' or dueBy value
    dependencies: {
      style: string; // css classes separated by space
    };
  };
}
